import { StyledSelect } from "@ando/ui";
import { ChevronDown, ChevronRight, Filter, History, ShieldCheck } from "lucide-react";
import * as React from "react";

import { Trans, useLingui } from "@lingui/react/macro";

import { Badge, cn } from "@ando/ui";

import type { HypermemAuditEvent } from "@ando/core";

import { useHypermemAudit } from "@/features/admin/hooks/useAdminHypermem";
import { formatTimestamp } from "@/features/admin/lib/adminFormat";

import { HmEmpty, HmError, HmLoading } from "./HmStates";

/**
 * HyperMem audit-trail tab (v2 port, Jun 6, 2026).
 *
 * Filterable viewer of ``agent_audit_events`` — an immutable chain
 *   intent → proposal → action → outcome → policy_change
 * tying every agent decision to who/what/when/for-whom.
 *
 * BE: ``GET /admin/hypermem/audit`` via ``useHypermemAudit``. Lazy: the query
 * fires only when this tab is mounted. Privacy posture mirrors v1 — ``user_id``
 * is masked (first 8 chars) by default and per-event metadata is expandable
 * inline rather than rendered eagerly.
 */

const ALL_EVENTS = "__all__";
const EVENT_LIMIT = 100;

const EVENT_TYPES: readonly string[] = [
  "action_executed",
  "action_rejected",
  "prompt_evolution",
  "prompt_approved",
  "prompt_rejected",
  "prompt_rollback",
  "pattern_detected",
  "admin_hypermem_query",
] as const;

type BadgeVariant = React.ComponentProps<typeof Badge>["variant"];

function eventBadgeVariant(event: string): BadgeVariant {
  if (event.includes("rejected") || event.includes("rollback")) {
    return "destructive";
  }
  if (event.includes("approved") || event.includes("executed")) {
    return "success";
  }
  if (event.includes("evolution") || event.includes("pattern")) {
    return "accent";
  }
  return "outline";
}

function maskUserId(userId: string | null): string {
  if (!userId) return "—";
  if (userId.length <= 8) return userId;
  return `${userId.slice(0, 8)}…`;
}

function hasMetadata(metadata: Record<string, unknown>): boolean {
  return !!metadata && Object.keys(metadata).length > 0;
}

function AuditRow({
  event,
}: {
  event: HypermemAuditEvent;
}): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const expandable = hasMetadata(event.metadata);

  return (
    <li className="rounded-xl border border-hairline bg-cream/40 p-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <Badge variant={eventBadgeVariant(event.event_type)}>{event.event_type}</Badge>
        {event.action_type && <span className="text-ink-1 text-sm">{event.action_type}</span>}
        {event.outcome && (
          <Badge variant="outline" className="text-xs">
            {event.outcome}
          </Badge>
        )}
        <span className="ml-auto shrink-0 text-ink-3 text-xs tabular-nums">
          {formatTimestamp(event.created_at)}
        </span>
      </div>

      <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-ink-3 text-xs">
        <span>
          <Trans>User:</Trans>{" "}
          <code className="text-ink-2" title={event.user_id ?? undefined}>
            {maskUserId(event.user_id)}
          </code>
        </span>
        {event.intent_id && (
          <span>
            <Trans>intent: {event.intent_id}</Trans>
          </span>
        )}
        {event.proposal_id && (
          <span>
            <Trans>proposal: {event.proposal_id}</Trans>
          </span>
        )}
        {event.policy_change_id && (
          <span>
            <Trans>policy: {event.policy_change_id}</Trans>
          </span>
        )}
      </div>

      {expandable && (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="flex items-center gap-1 font-medium text-ink-2 text-xs transition-colors hover:text-ink-1"
          >
            {open ? (
              <ChevronDown className="h-3.5 w-3.5" aria-hidden />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" aria-hidden />
            )}
            <Trans>Metadata</Trans>
          </button>
          {open && (
            <pre className="mt-2 max-h-72 overflow-auto whitespace-pre-wrap break-words rounded-lg border border-hairline bg-cream/60 p-3 text-[11px] text-ink-2 leading-snug">
              {JSON.stringify(event.metadata, null, 2)}
            </pre>
          )}
        </div>
      )}
    </li>
  );
}

export function HmAuditTrailTab({
  tenantId,
}: {
  tenantId: string;
}): React.ReactElement {
  const { t } = useLingui();
  const [filterEvent, setFilterEvent] = React.useState<string>(ALL_EVENTS);
  const [userIdInput, setUserIdInput] = React.useState("");
  const [appliedUserId, setAppliedUserId] = React.useState("");

  const args = React.useMemo(
    () => ({
      limit: EVENT_LIMIT,
      ...(filterEvent !== ALL_EVENTS ? { event_type: filterEvent } : {}),
      ...(appliedUserId.trim() ? { user_id: appliedUserId.trim() } : {}),
    }),
    [filterEvent, appliedUserId],
  );

  const { data, isLoading, isError, refetch } = useHypermemAudit(tenantId, args);

  const events = data?.events ?? [];
  const filtersActive = filterEvent !== ALL_EVENTS || !!appliedUserId;

  const applyUserFilter = () => setAppliedUserId(userIdInput);
  const clearFilters = () => {
    setFilterEvent(ALL_EVENTS);
    setUserIdInput("");
    setAppliedUserId("");
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h3 className="flex items-center gap-2 font-semibold text-ink-1 text-sm">
          <History className="h-4 w-4 text-ando-orange" aria-hidden />
          <Trans>Audit trail eventi agent</Trans>
        </h3>
        <p className="mt-1 text-ink-3 text-xs">
          <Trans>
            Cronologia immutabile di intent → proposal → action → outcome → policy_change. Ogni
            evento è linkato a chi ha fatto cosa, quando, per chi. user_id mascherato per default.
          </Trans>
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <label htmlFor="hm-audit-event" className="block text-ink-2 text-xs">
            <Trans>Tipo evento</Trans>
          </label>
          <StyledSelect
            id="hm-audit-event"
            value={filterEvent}
            onChange={(e) => setFilterEvent(e.target.value)}
            className="h-9 w-[220px] rounded-lg border border-hairline bg-cream/40 px-3 text-ink-1 text-sm focus:outline-none focus:ring-2 focus:ring-ando-orange/40"
          >
            <option value={ALL_EVENTS}>{t`Tutti`}</option>
            {EVENT_TYPES.map((ev) => (
              <option key={ev} value={ev}>
                {ev}
              </option>
            ))}
          </StyledSelect>
        </div>

        <div className="space-y-1">
          <label htmlFor="hm-audit-user" className="block text-ink-2 text-xs">
            <Trans>User ID (opzionale)</Trans>
          </label>
          <div className="flex gap-2">
            <input
              id="hm-audit-user"
              value={userIdInput}
              onChange={(e) => setUserIdInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") applyUserFilter();
              }}
              placeholder={t`UUID o vuoto`}
              className="h-9 w-[260px] rounded-lg border border-hairline bg-cream/40 px-3 text-ink-1 text-sm placeholder:text-ink-3 focus:outline-none focus:ring-2 focus:ring-ando-orange/40"
            />
            <button
              type="button"
              onClick={applyUserFilter}
              className="flex h-9 items-center gap-1.5 rounded-lg border border-hairline px-3 font-medium text-ink-2 text-sm transition-colors hover:bg-ink-3/10"
            >
              <Filter className="h-3.5 w-3.5" aria-hidden />
              <Trans>Filtra</Trans>
            </button>
            {filtersActive && (
              <button
                type="button"
                onClick={clearFilters}
                className="h-9 rounded-lg px-3 font-medium text-ink-2 text-sm transition-colors hover:bg-ink-3/10"
              >
                <Trans>Reset</Trans>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      {isLoading && <HmLoading label={<Trans>Caricamento audit trail…</Trans>} />}

      {isError && (
        <HmError
          message={<Trans>Impossibile caricare l'audit trail.</Trans>}
          onRetry={() => refetch()}
        />
      )}

      {!isLoading && !isError && events.length === 0 && (
        <HmEmpty
          icon={<ShieldCheck className="h-8 w-8 opacity-50" aria-hidden />}
          label={<Trans>Nessun evento per questi filtri.</Trans>}
        />
      )}

      {!isLoading && !isError && events.length > 0 && (
        <ul className={cn("space-y-2")}>
          {events.map((ev) => (
            <AuditRow key={ev.id} event={ev} />
          ))}
        </ul>
      )}
    </div>
  );
}
